import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  IconCheck,
  IconCreditCard,
  IconClockHour4,
  IconFileText,
  IconTruckDelivery,
  IconTool,
  IconFlag,
  IconTruckReturn,
  IconReceipt2,
  IconX,
} from '@tabler/icons-react';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { BookingPhaseKey, IBookingPhase, buildBookingTimeline } from '../helpers/bookingTimelineHelper';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatShortDateTime } from '../helpers/dateFormatHelper';
import { CORAL, SURFACE_1_SOFT, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Title, Text } = Typography;

// One icon per phase, same "a glyph you can read at a glance on a phone"
// criterion as BOOKING_STATUS_ICON -- the rail is narrow, so these are
// deliberately simple shapes rather than detailed ones.
const PHASE_ICON: Record<BookingPhaseKey, React.ElementType> = {
  quote: IconFileText,
  // Deliberately NOT a tick: this step is reached before the money is
  // taken, and a tick there reads as "done and paid".
  awaitingPayment: IconCreditCard,
  confirmed: IconCheck,
  transport: IconTruckDelivery,
  handover: IconFileText,
  inUse: IconTool,
  offRent: IconFlag,
  collection: IconTruckReturn,
  return: IconFileText,
  settlement: IconReceipt2,
};

// Diameter of a phase's own marker on the rail. The current phase gets a
// bigger one so "you are here" is findable without reading a single word --
// the whole point of having a rail rather than a plain list.
const MARKER_SIZE = 34;
const MARKER_SIZE_CURRENT = 42;

interface PhaseRowProps {
  phase: IBookingPhase;
  isLast: boolean;
  language: string;
}

const PhaseRow: React.FC<PhaseRowProps> = ({ phase, isLast, language }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const Icon = phase.state === 'cancelled' ? IconX : phase.state === 'upcoming' ? IconClockHour4 : PHASE_ICON[phase.key];
  const isCurrent = phase.state === 'current';
  const isDone = phase.state === 'done';
  const isCancelled = phase.state === 'cancelled';

  // Done reads teal (the app's settled/positive accent, same as the
  // confirmed booking card), current reads coral (the brand/attention
  // color), and anything still ahead stays deliberately quiet so the eye
  // lands on the current step first.
  const markerBackground = isDone ? TEAL_SOFT_BG : isCurrent ? CORAL : isCancelled ? token.colorErrorBg : SURFACE_1_SOFT;
  const markerColor = isDone ? TEAL : isCurrent ? '#fff' : isCancelled ? token.colorError : token.colorTextTertiary;
  const size = isCurrent ? MARKER_SIZE_CURRENT : MARKER_SIZE;

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
      {/* The rail: marker + the connector running down to the next phase.
          Fixed width so every title lines up regardless of marker size. */}
      <div style={{ width: MARKER_SIZE_CURRENT, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: markerBackground,
            color: markerColor,
            border: isCurrent ? 'none' : `1px solid ${isDone ? TEAL : token.colorBorderSecondary}`,
            boxShadow: isCurrent ? `0 4px 14px ${CORAL}40` : 'none',
            transition: 'background 200ms ease, box-shadow 200ms ease',
            flexShrink: 0,
          }}
        >
          <Icon size={isCurrent ? 20 : 16} />
        </div>
        {!isLast && (
          <div
            style={{
              flex: 1,
              width: 2,
              minHeight: 22,
              marginTop: 4,
              marginBottom: 4,
              borderRadius: 1,
              // A solid teal spine behind everything already done makes the
              // progress readable as a single continuous run, rather than a
              // row of unrelated dots.
              background: isDone ? TEAL : token.colorBorderSecondary,
              opacity: isDone ? 0.5 : 1,
            }}
          />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <Text
            strong={isCurrent || isDone}
            style={{
              fontSize: isCurrent ? 16 : 15,
              color: isCancelled ? token.colorTextTertiary : isCurrent ? token.colorText : undefined,
              textDecoration: isCancelled ? 'line-through' : undefined,
            }}
          >
            {translate(t, phase.labelKey)}
          </Text>
          {phase.at && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatShortDateTime(phase.at, language)}
            </Text>
          )}
        </div>
        {/* The explanation only earns its space on the step the user is
            actually on -- showing it on all nine turns the timeline into a
            wall of text on a phone. */}
        {isCurrent && (
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
            {translate(t, phase.descriptionKey)}
          </Text>
        )}
      </div>
    </div>
  );
};

interface BookingTimelineProps {
  booking: IBooking;
}

/**
 * The shared "where is my machine right now" view for a booking, rendered
 * identically for guest and host (both are looking at the same rental, and
 * a shared picture is what stops the two sides having different ideas about
 * what's happened). Phases come from buildBookingTimeline, which adapts them
 * to this specific booking's shape -- see that helper's own doc comment.
 *
 * Read-only on purpose: the actions that ADVANCE a phase (start transport,
 * sign the delivery note, declare end of work) live next to the phase they
 * belong to, not in here, so this component stays a pure picture of state.
 */
const BookingTimeline: React.FC<BookingTimelineProps> = ({ booking }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const phases = buildBookingTimeline(booking);

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 16px' }}>
        {translate(t, TRANSLATIONS.BOOKING_TIMELINE_TITLE)}
      </Title>
      {phases.map((phase, index) => (
        <PhaseRow key={phase.key} phase={phase} isLast={index === phases.length - 1} language={i18n.language} />
      ))}
    </div>
  );
};

export default BookingTimeline;
